import { addDays, isPlainDate, startOfWeek } from './dates.js';
export const repeatDays = ['MO', 'TU', 'WE', 'TH', 'FR', 'SA', 'SU'];
/** Deliberately bounded, date-only RRULE subset. Unknown properties are rejected. */
export function parseRecurrence(value) {
    const entries = value.split(';').map((part) => part.split('='));
    if (entries.some((p) => p.length !== 2) ||
        new Set(entries.map((p) => p[0])).size !== entries.length)
        return null;
    const fields = Object.fromEntries(entries);
    if (Object.keys(fields).some((k) => !['FREQ', 'INTERVAL', 'BYDAY', 'BYMONTHDAY', 'UNTIL'].includes(k)))
        return null;
    const frequency = fields['FREQ'];
    if (frequency !== 'DAILY' &&
        frequency !== 'WEEKLY' &&
        frequency !== 'MONTHLY' &&
        frequency !== 'YEARLY')
        return null;
    const rawInterval = fields['INTERVAL'] ?? '1';
    if (!/^\d{1,3}$/.test(rawInterval))
        return null;
    const interval = Number(rawInterval);
    if (interval < 1 || interval > 365)
        return null;
    const days = fields['BYDAY']?.split(',') ?? [];
    if (fields['BYDAY'] !== undefined &&
        (frequency !== 'WEEKLY' ||
            days.some((d) => !repeatDays.includes(d)) ||
            new Set(days).size !== days.length))
        return null;
    const monthDay = fields['BYMONTHDAY'] === undefined ? null : Number(fields['BYMONTHDAY']);
    if (monthDay !== null &&
        (frequency !== 'MONTHLY' ||
            !/^\d{1,2}$/.test(fields['BYMONTHDAY']) ||
            monthDay < 1 ||
            monthDay > 31))
        return null;
    let until = null;
    if (fields['UNTIL'] !== undefined) {
        if (!/^\d{8}$/.test(fields['UNTIL']))
            return null;
        const raw = fields['UNTIL'];
        until = `${raw.slice(0, 4)}-${raw.slice(4, 6)}-${raw.slice(6, 8)}`;
        if (!isPlainDate(until))
            return null;
    }
    return { frequency, interval, days: days, monthDay, until };
}
export function formatRecurrence(rule) {
    return [
        `FREQ=${rule.frequency}`,
        `INTERVAL=${rule.interval}`,
        ...(rule.days.length ? [`BYDAY=${rule.days.join(',')}`] : []),
        ...(rule.monthDay ? [`BYMONTHDAY=${rule.monthDay}`] : []),
        ...(rule.until ? [`UNTIL=${rule.until.replaceAll('-', '')}`] : []),
    ].join(';');
}
/** The anchor is the nominal occurrence date, independent of rescheduling. */
export function nextOccurrence(value, anchor) {
    const rule = parseRecurrence(value);
    if (!rule || !isPlainDate(anchor))
        return null;
    const accept = (date) => isPlainDate(date) && date > anchor && (!rule.until || date <= rule.until) ? date : null;
    if (rule.frequency === 'DAILY')
        return accept(addDays(anchor, rule.interval));
    if (rule.frequency === 'WEEKLY') {
        const monday = startOfWeek(anchor, 1);
        const offsets = rule.days.length
            ? rule.days.map((d) => repeatDays.indexOf(d)).sort((a, b) => a - b)
            : [(new Date(`${anchor}T00:00:00Z`).getUTCDay() + 6) % 7];
        for (const offset of offsets) {
            const date = addDays(monday, offset);
            if (date > anchor)
                return accept(date);
        }
        return accept(addDays(monday, rule.interval * 7 + offsets[0]));
    }
    const [year, month, day] = anchor.split('-').map(Number);
    // Skip impossible dates, including 31st and February 29; never clamp them.
    for (let step = 1; step <= 4800; step++) {
        const months = month - 1 + (rule.frequency === 'MONTHLY' ? rule.interval * step : 0);
        const y = year + (rule.frequency === 'YEARLY' ? rule.interval * step : Math.floor(months / 12));
        if (y > 9999)
            return null;
        const m = (months % 12) + 1;
        const d = rule.frequency === 'MONTHLY' ? (rule.monthDay ?? day) : day;
        const date = `${String(y).padStart(4, '0')}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
        if (rule.until && date > rule.until)
            return null;
        if (isPlainDate(date))
            return accept(date);
    }
    return null;
}
export function remainingOccurrences(value, anchor) {
    const rule = parseRecurrence(value);
    if (!rule?.until || !isPlainDate(anchor) || anchor > rule.until)
        return 0;
    if (rule.frequency === 'DAILY') {
        return 1 + Math.floor((Date.parse(rule.until) - Date.parse(anchor)) / 86400000 / rule.interval);
    }
    if (rule.frequency === 'WEEKLY') {
        const monday = startOfWeek(anchor, 1);
        const anchorOffset = Math.round((Date.parse(anchor) - Date.parse(monday)) / 86400000);
        const endOffset = Math.round((Date.parse(rule.until) - Date.parse(monday)) / 86400000);
        const offsets = rule.days.length
            ? rule.days.map((day) => repeatDays.indexOf(day))
            : [anchorOffset];
        return (1 +
            offsets.reduce((count, offset) => count +
                (offset > anchorOffset && offset <= endOffset ? 1 : 0) +
                Math.max(0, Math.floor((endOffset - offset) / (rule.interval * 7))), 0));
    }
    let count = 1;
    let date = nextOccurrence(value, anchor);
    while (date && count <= 10_000) {
        count++;
        date = nextOccurrence(value, date);
    }
    return count;
}
export function validRecurrenceAnchor(value, anchor) {
    const rule = parseRecurrence(value);
    if (!rule || !anchor || !isPlainDate(anchor) || (rule.until && rule.until < anchor))
        return false;
    if (rule.frequency === 'MONTHLY' && rule.monthDay && Number(anchor.slice(8)) !== rule.monthDay)
        return false;
    if (rule.days.length &&
        !rule.days.includes(repeatDays[(new Date(`${anchor}T00:00:00Z`).getUTCDay() + 6) % 7]))
        return false;
    return !rule.until || remainingOccurrences(value, anchor) <= 10_000;
}
//# sourceMappingURL=recurrence.js.map